import { useApi } from '@hero/context';
import type {
  UseOrgProfileApi,
  UseOrgProfileUpdateApi,
  UseOrgProfileAdminUpdateApi,
} from './types';
import { useQueryAPI, useMutationAPI } from '../../utils';

/**
 * Get the current user's org profile (flat `OrganizationProfile`
 * projection composed server-side from `Org + Bank`). The shape is
 * identical to the legacy `useOrganizationProfileApi` response so
 * existing FE form initial-value code keeps working unchanged.
 *
 * Cache invalidation flows through the `orgProfile` entity in
 * `ENTITY_CONFIGS`, which also busts the legacy `organization` key
 * during the transition.
 *
 * Hook API: {@link UseOrgProfileApi}
 */
export const useOrgProfileApi: UseOrgProfileApi = apiConfig => {
  const api = useApi();
  return useQueryAPI({
    entity: 'orgProfile',
    apiFn: api.orgProfile.get,
    query: undefined,
    apiConfig,
  });
};

/**
 * Section-keyed partial update on the current user's org. The body
 * is `{ displayName?, logo?, legal?, bank?, settings? }` and the
 * backend writes ONLY the keys present — sending
 * `{ bank: { IBAN: 'SA…' } }` updates `Bank.IBAN` and touches
 * nothing else. See `OrgProfileService` for the dotted-path-merge
 * guarantees.
 *
 * Hook API: {@link UseOrgProfileUpdateApi}
 */
export const useOrgProfileUpdateApi: UseOrgProfileUpdateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'orgProfile',
    apiFn: api.orgProfile.update,
    apiConfig,
  });
};

/**
 * Admin: section-keyed partial update on any org by id. Backend
 * route is `@SuperAdmin()`-gated. Used by the admin-panel
 * `EditOrganizationProfile` page.
 *
 * Hook API: {@link UseOrgProfileAdminUpdateApi}
 */
export const useOrgProfileAdminUpdateApi: UseOrgProfileAdminUpdateApi = apiConfig => {
  const api = useApi('admin');
  return useMutationAPI({
    entity: 'orgProfile',
    apiFn: api.orgProfile.adminUpdate,
    apiConfig,
  });
};
